import { isContentfulEntry } from 'remote-com-shared/domains/contentful/helpers';
import { getEntryMeta } from 'remote-com-shared/domains/contentful/prismicBridge';
import { defaultLocale } from 'remote-com-shared/domains/locales/config';

const basePath = process.env.NEXT_PUBLIC_BASE_PATH || '';
const baseUrl = process.env.NEXT_PUBLIC_BASE_URL || '';

export function getDocumentMeta(document) {
  if (isContentfulEntry(document)) {
    return getEntryMeta(document);
  }
  if (document._meta) {
    return document._meta;
  }
  return document;
}

export function getHrefLocalePrefix(document) {
  const { lang } = getDocumentMeta(document);
  if (!lang) {
    return '';
  }

  const normalizedLang = lang.toLowerCase();

  if (normalizedLang === defaultLocale) {
    return '';
  }

  return `/${normalizedLang}`;
}

export function pandaHrefResolver(document) {
  const { type, lang } = getDocumentMeta(document);
  const localePrefix = getHrefLocalePrefix(document);

  switch (type) {
    case 'homepage_overview_and_why_remote_sections':
    case 'client_logos':
    case 'banner_sitewide':
    case 'header_navigation_sitewide':
    case 'footer_navigation_sitewide':
    case 'currency':
    case 'homepage_new':
    case 'cookie_control_config':
      return `${localePrefix}/`;
    case 'country_explorer':
    case 'country':
      return `${localePrefix}/country-explorer/[uid]`;
    case 'country_hiring_guide':
      return `${localePrefix}/customer-resources/country-hiring-guide/[uid]`;
    case 'competitor_comparison_hub':
      return `${localePrefix}/compare-remote`;
    case 'competitor_comparison':
      return `${localePrefix}/compare-remote/[uid]`;
    case 'consulting_services':
      return `${localePrefix}/services/consulting`;
    case 'partner_landing_page':
      return `${localePrefix}/partners/[uid]`;
    case 'vc_page':
      return `${localePrefix}/vcstartups/[uid]`;
    case 'vc_tier_2':
      return `${localePrefix}/vcstartups`;
    case 'about_remote':
      return `${localePrefix}/about`;
    case 'remote_for_ref':
      return `${localePrefix}/remote-for-refugees`;
    case 'remote_work_destinations':
      return `${localePrefix}/best-destinations-remote-work`;
    case 'paid_marketing_promo_codes':
    case 'paid_marketing_campaign_lp':
      return `${localePrefix}/[uid]`;
    case 'resources_main_page':
      return `${localePrefix}/resources`;
    case 'resources_page':
      return `${localePrefix}/resources/[uid]`;
    case 'get_started':
      return `${localePrefix}/get-started`;
    case 'affiliates_landing_page':
      return `${localePrefix}/affiliates`;
    case 'affiliates_hr':
      return `${localePrefix}/affiliates/hr`;
    case 'partners_landing_view':
      return `${localePrefix}/partners`;
    case 'marketplace_partner_interest':
      return `${localePrefix}/partners/marketplace`;
    case 'influencer_report_2021':
      return `${localePrefix}/influencer-report-2021`;
    case 'influencer_report_2022':
      return `${localePrefix}/influencer-report-2022`;
    case 'influencer_report_2023_clone_of_2022':
      return `${localePrefix}/remote-influencer-report`;
    case 'community_survey':
      return `${localePrefix}/communitysurvey`;
    case 'get_started_landing_page':
      return `${localePrefix}/lp/[uid]`;
    case 'community_partnerships_lp':
      return `${localePrefix}/community-partnerships`;
    case 'remote_connect_2022_event':
      return `${localePrefix}/connect-2022`;
    case 'webinar_page':
      return `${localePrefix}/webinars/[uid]`;
    case 'new_integrations_page':
      return `${localePrefix}/integrations`;
    case 'reimagine_remote':
      return `${localePrefix}/ichooseremote`;
    case 'superior__customer_experience_page':
      return `${localePrefix}/platform/customer-experience`;
    case 'remote_for_good':
      return `${localePrefix}/socialpurpose`;
    case 'open_entity_cost_page':
      return `${localePrefix}/real-cost`;
    case 'vc_partners_overview':
      return `${localePrefix}/vc-partners`;
    case 'head_of_remote_whitepaper':
      return `${localePrefix}/head-of-remote-whitepaper`;
    case 'vertical_referral_program_template':
      return `${localePrefix}/referrals/[uid]`;
    case 'data_protection':
      return `${localePrefix}/data-protection-guard`;
    case 'api-eguide-template':
      return `${localePrefix}/downloadable-resources/[uid]`;
    case 'global_employee_api':
      return `${localePrefix}/platform/remote-api`;
    case 'community_waitlist':
      return `${localePrefix}/community-waitlist`;
    case 'contact_us_page':
      return `${localePrefix}/contact-us`;
    case 'services_eor':
      return `${localePrefix}/services/employer-of-record`;
    case 'services_global_payroll':
      return `${localePrefix}/services/global-payroll`;
    case 'startup-founders':
      return `${localePrefix}/platform/use-case/startup-founders`;
    case 'country_availability_status':
    case 'country_explorer_overvi':
      return `${localePrefix}/country-explorer`;
    case 'global_payroll':
      return `${localePrefix}/platform/run-payroll`;
    case 'global_benefits':
      return `${localePrefix}/platform/global-benefits`;
    case 'integrations_for_customers':
      return `${localePrefix}/platform/integrations-for-customers`;
    case 'pricing_overview':
    case 'pricing_overview_cost':
      return `${localePrefix}/pricing`;
    case 'platform_use_case_page':
      return `${localePrefix}/platform/use-case/[uid]`;
    case 'startups':
      return `${localePrefix}/platform/use-case/startup`;
    case 'platform_page':
      return `${localePrefix}/platform/[uid]`;
    case 'services_page':
      return `${localePrefix}/services/[uid]`;
    case 'gdpr_erasure_page':
      return `${localePrefix}/contact-data-protection`;
    case 'remote_relocation_page':
      return `${localePrefix}/remote-relocation`;
    case 'careers_page':
      return `${localePrefix}/careers`;
    case 'misclassification_page':
      return `${localePrefix}/employee-contractor-misclassification-risk-calculator`;
    case 'contractor_management':
      return `${localePrefix}/platform/hire-international-contractors`;
    case 'future_employee_benefits':
      switch (lang) {
        case 'de-de':
          return `${localePrefix}/moderne-mitarbeiter-benefits`;
        case 'fr-fr':
          return `${localePrefix}/avantages-sociaux-du-futur`;
        default:
          return `${localePrefix}/future-employee-benefits`;
      }
    case 'skills_gap_page':
      return `${localePrefix}/uk-employers-recruiting-talent-despite-struggles`;
    case 'services_hub':
      return `${localePrefix}/services`;
    case 'future_of_wfh':
      return `${localePrefix}/future-work-from-home`;
    case 'api_partner_portal_overview':
      return `${localePrefix}/platform/remote-api/partner-portal`;
    case 'api_partner_portal_page':
      return `${localePrefix}/platform/remote-api/partner-portal/[uid]`;
    case 'employer_of_record':
      return `${localePrefix}/platform/hire-international-employees`;
    case 'customer_event_page':
      return `${localePrefix}/customer-events/[uid]`;
    case 'g2_gated_asset':
      return `${localePrefix}/g2-multi-country-payroll-report `;
    case 'g2_leader_v2':
      return `${localePrefix}/g2-returning`;
    case 'employment_faq':
      return `${localePrefix}/employment-faq `;
    case 'request_api_docs_resource':
      return `${localePrefix}/resources/api/request`;
    case 'request_portal_access_resource':
      return `${localePrefix}/resources/api/portal-access`;
    case 'stock_options':
      return `${localePrefix}/stock-options`;
    case 'refer_a_friend_welcome':
      return `${localePrefix}/refer-a-friend-welcome`;
    case 'benefits_one_pager':
      return `${localePrefix}/benefits-guide/[uid]`;
    case 'international_global_payroll_processing_guide':
      return `${localePrefix}/international-payroll-processing-guide`;
    case 'submit_referral_affiliates':
      return `${localePrefix}/affiliates/submit-referral`;
    case 'submit_referral_partners':
      return `${localePrefix}/partners/become-a-partner`;
    case 'greenhouse_remote_life_work_balance_guide':
      return `${localePrefix}/resources/greenhouse-remote-life-work-balance-guide`;
    case 'research_resource_page':
      return `${localePrefix}/resources/research`;
    case 'case_studies_resource_page':
      return `${localePrefix}/resources/case-studies`;
    case 'webinarresourcepage':
      return `${localePrefix}/resources/webinars`;
    case 'employee_cost_calculator_lp':
      return `${localePrefix}/employee-cost-calculator`;
    case 'customer_onboarding_handbook':
      return `${localePrefix}/customer-onboarding-handbook`;
    case 'openings_page':
      return `${localePrefix}/openings`;
    case 'openings_page/[jobId]':
      return `${localePrefix}/openings/[jobId]`;
    case 'services_contractor_management':
      return `${localePrefix}/services/contractor-management`;
    case 'upsell_convert_contractor_to_employee':
      return `${localePrefix}/lp/convert-contractors-to-employees`;
    case 'country_contractor_management':
      return `${localePrefix}/services/contractor-management/[uid]`;
    case 'platform_hub':
      return `${localePrefix}/platform`;
    case 'disclaimerPage':
      return `${localePrefix}/[uid]`;
    case 'global_recruiting_and_employment':
      return `${localePrefix}/services/global-recruitment`;
    case 'api_customer_portal_overview':
      return `${localePrefix}/platform/remote-api/customer-portal`;
    case 'api_customer_portal_page':
      return `${localePrefix}/platform/remote-api/customer-portal/[uid]`;
    case 'influencer_report_2023':
      return `${localePrefix}/influencer-report-sign-up`;
    case 'business_size_page':
      return `${localePrefix}/business-size/[uid]`;
    case 'life-work_balance_report_us':
      return `${localePrefix}/us-states-life-work-balance-index`;
    case 'contractor_landing_page':
      return `${localePrefix}/lp-contractor`;
    case 'solutions_page':
      return `${localePrefix}/solutions/[uid]`;
    case 'parental_leave_report':
      return `${localePrefix}/resources/research/best-inclusive-parental-leave-policies`;
    case 'parental_leave_policy':
      return `${localePrefix}/resources/equitable-paid-parental-leave-policy`;
    case 'nominate_your_company':
      return `${localePrefix}/nominate-your-company`;
    case 'benefits_directory':
      return `${localePrefix}/benefits-directory`;
    case 'workforce_report':
      return `${localePrefix}/remote-workforce-report`;
    case 'employment_faq_employees':
      return `${localePrefix}/preemployment-faq`;
    case 'switch_to_remote':
      return `${localePrefix}/switch-to-remote`;
    case 'country_hiring_considerations':
      return `${localePrefix}/country-hiring-considerations`;
    case 'lp_contractor_fair_pricing':
      return `${localePrefix}/lp/contractor-fairpricing`;
    case 'paid_lp_contractor_fair_pricing':
      return `${localePrefix}/get-started/global-contractor`;
    case 'g2_returning_apac':
      return `${localePrefix}/g2-returning-apac`;
    case 'life-work_balance_report_global':
      return `${localePrefix}/resources/research/global-life-work-balance-index`;
    case 'life-work_balance_report_parents':
      return `${localePrefix}/resources/research/parental-life-work-balance-index`;
    default:
      return null;
  }
}

export function blogHrefResolver(document) {
  const { type } = getDocumentMeta(document);
  const localePrefix = getHrefLocalePrefix(document);

  switch (type) {
    case 'company_blog_category':
    case 'blogPostCategory':
    case 'BlogPostCategory':
      return `${localePrefix}/category/[uid]`;
    case 'author':
    case 'Author':
      return `${localePrefix}/author/[uid]`;
    case 'company_blog_post':
    case 'blogPost':
    case 'BlogPost':
      return `${localePrefix}/[uid]`;
    case 'blogOverview':
    case 'BlogOverview':
      return `${localePrefix}`;
    default:
      return null;
  }
}

export function marketplaceHrefResolver(document) {
  const { type } = getDocumentMeta(document);
  switch (type) {
    case 'talent':
      return '/recruiters/[uid]';
    case 'partner':
      return '/[uid]';
    default:
      return null;
  }
}

export function sharedHrefResolver(document) {
  let resolverPrefix = '';
  let resolverPath = null;
  const resolverName = process.env.NEXT_PUBLIC_RESOLVER_NAME;

  // Prismic link web
  if (document?.linkType === 'Link.web') {
    return document.url;
  }

  const pandaHref = pandaHrefResolver(document);
  if (pandaHref) {
    resolverPath = pandaHref;
    if (resolverName !== 'panda') {
      resolverPrefix = process.env.NEXT_PUBLIC_PANDA_BASE_URL;
    }
  }

  const blogHref = blogHrefResolver(document);
  if (blogHref !== null) {
    resolverPath = blogHref;
    if (resolverName !== 'blog') {
      resolverPrefix = process.env.NEXT_PUBLIC_BLOG_BASE_URL;
    }
  }

  const marketplaceHref = marketplaceHrefResolver(document);
  if (marketplaceHref) {
    resolverPath = marketplaceHref;
    if (resolverName !== 'marketplace') {
      resolverPrefix = process.env.NEXT_PUBLIC_MARKETPLACE_BASE_URL;
    }
  }

  if (resolverPath === null) {
    const { type, uid } = getDocumentMeta(document);
    if (isContentfulEntry(document)) {
      throw new Error(`Resolver not found for Contentful type "${type}" and slug "${uid}"`);
    } else {
      throw new Error(`Resolver not found for Prismic type "${type}" and uid "${uid}"`);
    }
  }

  return `${resolverPrefix}${resolverPath}`;
}

export function sharedLinkResolver(document) {
  const { type, uid } = getDocumentMeta(document);
  switch (type) {
    default:
      return sharedHrefResolver(document).replace('[uid]', uid);
  }
}

export function absoluteLinkResolver(document) {
  return basePath + sharedLinkResolver(document);
}

// qualified is used in places that require the whole url, including the protocol and domain.
// This baseUrl value will also include any base path, if needed, so we use the link resolver directly
// instead of using absoluteLinkResolver
export function qualifiedLinkResolver(document) {
  return baseUrl + sharedLinkResolver(document);
}
